import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {Settings} from '../models/settings.model';
import {tap} from 'rxjs';
import {AlertModalService} from '../component/modal';
import {AnalyticsService} from './analytics.service';

@Injectable({
    providedIn: 'root'
})
export class AppConfigService {
    title = 'Application Configuration Service';
    settings: Settings;
    appConfigs = {};
    getArcgisBaseUrl() {
        return 'https://arcgis' + this.settings.appBiome + '.' + this.settings.appEnv + '.plan.koondart.com';
    }

    getSlipEnv() {
        return this.settings.appEnv === 'prod' ? 'landgate-arcgis-site.app.landgate.wa.gov.au' : 'landgate-arcgis-site.uat.app.landgate.wa.gov.au';
    }

    constructor(
        private http: HttpClient,
        private alertModalService: AlertModalService,
        private analyticsService: AnalyticsService
    ) {}

    getAppSettings() {
        return this.settings;
    }

    loadSettings() {
        return this.http.get<Settings>('assets/settings.json').pipe(
            tap(settings => {
                this.settings = settings;
                console.log('Settings obtained');
                this.analyticsService.setupGoogleAnalytics(settings.appEnv);
            })
        );
    }

    getApplicationConfiguration() {
        console.log(this.title + ' getApplicationConfiguration');
        return this.http.get(`${this.settings.apiGatewayUrl}/config/release/version`, {transferCache: false}).pipe(
            tap({
                next: res => {
                    console.debug(`${this.title} - getApplicationConfiguration success`);
                    Object.keys(res).forEach(k => (this.appConfigs[k] = res[k]));
                },
                error: error => {
                    console.error(`${this.title} - getApplicationConfiguration error:`, error);
                    this.alertModalService.createAlertMessage('Error loading app configs', 'Cancel');
                }
            })
        );
    }

    getReleaseVersion() {
        return this.appConfigs['release.version'];
    }
    getDesignProperties() {
        return {
            BASE_URL: this.getArcgisBaseUrl(),
            GEOMETRY_SERVER_URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/Utilities/Geometry/GeometryServer',
            UPLOADS_PREFIX: 'Spatial',
            DESIGN_SETTINGS: {
                MAP_SPATIAL_REFERENCE_GDA94: 4283,
                MAP_SPATIAL_REFERENCE_GDA2020: 7844,
                MAP_PROJECTED_SPATIAL_REFERENCE_GDA94: 28350,
                MAP_PROJECTED_SPATIAL_REFERENCE_GDA2020: 7850,
                MAP_SPATIAL_REFERENCE_PLANAR: 4326,
                WA_EXTENT_GDA94: {
                    X_MIN: -206688.349,
                    Y_MIN: 5966851.141,
                    X_MAX: 2121649.641,
                    Y_MAX: 8538606.285,
                    WKID: 28350
                },
                WA_EXTENT_GDA2020: {
                    X_MIN: -206688.349,
                    Y_MIN: 5966851.141,
                    X_MAX: 2121649.641,
                    Y_MAX: 8538606.285,
                    WKID: 7850
                },
                DEFAULT_MAP: {
                    ID: 'mainMap',
                    OPTIONS: {
                        displayGraphicsOnPan: false,
                        fadeOnZoom: false,
                        isClickRecenter: false,
                        isDoubleClickZoom: false,
                        isPan: false,
                        logo: false,
                        slider: false,
                        showLabels: false,
                        smartNavigation: false
                    },
                    MAP_SERVICE_LAYER: {
                        URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_MapService',
                        OPTIONS: {
                            id: 'mapServiceLayer'
                        }
                    },
                    LAYERS: {
                        ANGLE: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/0',
                            OPTIONS: {
                                id: 'angleFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT,
                            }
                        },
                        ANNOTATION: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/1',
                            OPTIONS: {
                                id: 'annotationFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        POINT: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/2',
                            OPTIONS: {
                                id: 'pointFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        CONTROL_POINT: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/2',
                            OPTIONS: {
                                id: 'controlPointFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT,
                            }
                        },
                        CONSTRUCT_POINT: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/3',
                            OPTIONS: {
                                id: 'constructPointFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        CONSTRUCT_LINE: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/4',
                            OPTIONS: {
                                id: 'constructLineFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        LINE: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/5',
                            OPTIONS: {
                                id: 'lineFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        POLYGON: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/6',
                            OPTIONS: {
                                id: 'polygonFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        LOT: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/7',
                            OPTIONS: {
                                id: 'lotFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        ROAD: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/8',
                            OPTIONS: {
                                id: 'roadFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        INTEREST: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/9',
                            OPTIONS: {
                                id: 'interestFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        INTEREST_LINE: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/5',
                            OPTIONS: {
                                id: 'polylineFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        ENLARGEMENT: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/10',
                            OPTIONS: {
                                id: 'enlargementFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        CLIPPING: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/11',
                            OPTIONS: {
                                id: 'clippingFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        AFFECTED_LOT: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/12',
                            OPTIONS: {
                                id: 'affectedLotFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        },
                        PRINT_AREA: {
                            URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/PlanSubmissions_FeatureService/FeatureServer/13',
                            OPTIONS: {
                                id: 'printAreaFeatureLayer',
                                outFields: ['*'],
                                mode: 0 // FeatureLayer.MODE_SNAPSHOT
                            }
                        }
                    }
                },
                SLIP: {
                    MAX_SCALE: 100,
                    ADMIN: {
                        BLACK_OPTIONS: {
                            id: 'slipBlackLayer',
                            opacity: 1,
                            showAttribution: false
                        },
                        WHITE_OPTIONS: {
                            id: 'slipWhiteLayer',
                            opacity: 1,
                            showAttribution: false
                        },
                        LAYERS: [1, 10, 20, 21, 22, 23, 30, 31, 70, 71, 80, 81, 72, 73, 85, 86]
                    },
                    SLIP_TIMEOUT: 15000,
                    SLIP_MESSAGE:
                        '<style>\n' +
                        'ul li {\n' +
                        '    list-style: circle inside; margin-left: 1%; text-align:left\n' +
                        '}' +
                        'ul li ul li {\n' +
                        '    list-style: none inside;margin-left: 5%; text-align:left; font-style: italic;\n' +
                        '}' +
                        'div {\n' +
                        '    text-align: justify;\n' +
                        '    text-justify: inter-word;\n' +
                        '    font-weight: normal;\n' +
                        '}\n' +
                        '</style>' +
                        '<div>' +
                        '<p>Location data services could not be reached or data could ' +
                        'not be retrieved due to a high number of data requests.</p>' +
                        '<p style="color:red;">Although location data services are not available, ' +
                        'the Portal remains functional and you may continue to create, edit, assign and lodge plans.</p>' +
                        '<p>The functions impacted include:' +
                        '<ul>' +
                        '    <li>Analysis page' +
                        '        <ul>' +
                        '            <li>-&nbsp;&nbsp;will not refresh the list of dependent plans.</li>' +
                        '        </ul>' +
                        '    </li>' +
                        '    <p></p>' +
                        '    <li>Design page' +
                        '        <ul>' +
                        '            <li>-&nbsp;&nbsp;will not display cadastral layer.</li>' +
                        '            <li>-&nbsp;&nbsp;will not allow import of survey data in plane coordinates.</li>' +
                        '        </ul>' +
                        '    </li>' +
                        '</ul>' +
                        '</p>' +
                        '<p>Should this problem persist, please refer to the MyLandgate Survey' +
                        ' Channel for outage updates or call Customer Care for support.</p>' +
                        '</div>'
                }
            },
            DESIGN_WIDGETS_CONFIGURATION: {
                IMPORT_SURVEY_DATA: {
                    FILE_TYPES: [
                        {
                            FILE_TYPE: 'CSD',
                            GP_URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/ImportCSDFile/GPServer/ImportCSDFile'
                        },
                        {
                            FILE_TYPE: 'DWG',
                            GP_URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/ImportDWGFile/GPServer/ImportDWGFile'
                        },
                        {
                            FILE_TYPE: 'ASCII',
                            GP_URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/ImportASCIIPoints/GPServer/ImportASCIIPoints'
                        }
                    ]
                },
                EXPORTPLAN: {
                    GP_URL: this.getArcgisBaseUrl() + '/arcgis/rest/services/NLRP/ExportPlan/GPServer/ExportPlan'
                }
            },
            DESIGN_CURSORS_CONFIGURATION: {
                CURSOR_SELECT_BLACK: '/cursors/white-cursor-cross.cur',
                CURSOR_PAN_BLACK: '/cursors/white-cursor-pan.cur',
                CURSOR_PENCIL_BLACK: '/cursors/white-cursor-pencil.cur',
                CURSOR_CROSS_BLACK: '/cursors/white-cursor-cross.cur',
                CURSOR_SELECT_WHITE: '/cursors/black-cursor-cross.cur',
                CURSOR_PAN_WHITE: '/cursors/black-cursor-pan.cur',
                CURSOR_PENCIL_WHITE: '/cursors/black-cursor-pencil.cur',
                CURSOR_CROSS_WHITE: '/cursors/black-cursor-cross.cur',
                CURSOR_MOVE_BLACK: '/cursors/white-cursor-move.cur',
                CURSOR_MOVE_WHITE: '/cursors/black-cursor-move.cur'
            },
            SPATIAL_TYPES: {
                AFFECTED_LOT: 'AFFECTEDLOT',
                ANNOTATION: 'ANNOTATION',
                ANGLE: 'ANGLE',
                CLIPPING: 'CLIPPING',
                CONSTRUCT_LINE: 'CONSTRUCTLINE',
                CONSTRUCT_POINT: 'CONSTRUCTPOINT',
                ENLARGEMENT: 'ENLARGEMENT',
                INTEREST: 'INTEREST',
                LINE: 'LINE',
                LOT: 'LOT',
                POINT: 'POINT',
                POLYGON: 'POLYGON',
                PRINT_AREA: 'PRINTAREA'
            },
            EVENTS: {
                FEATURES_CREATED: 'featuresCreated',
                POINT_FEATURES_UPDATED: 'pointFeaturesUpdated',
                GEOMETRY_FEATURES_UPDATED: 'geometryFeaturesUpdated',
                ANGLE_UPDATE: {
                    IRRELEVANT_ATTRIBUTES_ON_UPDATE: [
                        'objectid',
                        'id',
                        'parentid',
                        'origx',
                        'origy',
                        'x',
                        'y',
                        'z',
                        'enlargementid',
                        'enlargementids',
                        'visible',
                        'anglerotation',
                        'anglerotationdms'
                    ]
                }
            },
            VIEW_MODES: {
                BLACK_BACKGROUND_MODE: 'Black Background Mode',
                WHITE_BACKGROUND_MODE: 'White Background Mode',
                PRINT_PREVIEW_MODE: 'Print Preview Mode',
                BLACK_LAYERS_VISIBLE: [0, 2, 4, 8, 12, 16, 21, 25, 28, 31],
                BLACK_LAYERS_ALL: [0, 2, 4, 8, 10, 12, 14, 16, 18, 21, 23, 25, 27, 28, 30, 31],
                BLACK_LAYERS_ANNOTATION_HIDDEN_VISIBLE: [0, 2, 4, 8, 10, 12, 14, 16, 21, 25, 28, 31],
                BLACK_LAYERS_GEOMETRY_HIDDEN_VISIBLE: [0, 2, 4, 8, 12, 16, 18, 21, 23, 25, 27, 28, 30, 31],
                WHITE_LAYERS_VISIBLE: [0, 3, 5, 9, 13, 17, 22, 26, 29, 31],
                WHITE_LAYERS_ALL: [0, 3, 5, 9, 10, 13, 14, 17, 19, 22, 24, 26, 29, 30, 31],
                WHITE_LAYERS_ANNOTATION_HIDDEN_VISIBLE: [0, 3, 5, 9, 10, 13, 14, 17, 22, 26, 29, 31],
                WHITE_LAYERS_GEOMETRY_HIDDEN_VISIBLE: [0, 3, 5, 9, 13, 17, 19, 22, 24, 26, 27, 29, 30, 31],
                PRINT_PREVIEW_ANNOTATION_HIDDEN_VISIBLE_MAINPLAN: [0, 6, 7, 10, 11, 14, 15, 20, 29, 31],
                PRINT_PREVIEW_GEOMETRY_HIDDEN_VISIBLE_MAINPLAN: [0, 6, 7, 11, 15, 19, 20, 24, 26, 27, 29, 30, 31],
                PRINT_PREVIEW_ANNOTATION_HIDDEN_VISIBLE_ENLARGEMENT: [1, 7, 10, 11, 14, 15, 20, 29, 31],
                PRINT_PREVIEW_GEOMETRY_HIDDEN_VISIBLE_ENLARGEMENT: [1, 7, 11, 15, 19, 20, 24, 26, 27, 29, 30, 31],
                PRINT_PREVIEW_LAYERS_VISIBLE_MAINPLAN: [0, 6, 7, 11, 15, 20, 26, 29, 31],
                PRINT_PREVIEW_LAYERS_VISIBLE_ENLARGEMENT: [1, 7, 11, 15, 20, 26, 29, 31],
                PRINT_PREVIEW_LAYERS_ALL_MAINPLAN: [0, 6, 7, 10, 11, 14, 15, 19, 20, 24, 26, 27, 29, 30, 31],
                PRINT_PREVIEW_LAYERS_ALL_ENLARGEMENT: [1, 7, 10, 11, 14, 15, 19, 20, 24, 26, 27, 29, 30, 31]
            },
            VIEW_MODE_RESOLUTIONS: [
                {type: 'HIGH', value: 'SVG', displayValue: 'High (Vector graphics)', toolTip: 'Select for high bandwidth internet connections.'},
                {type: 'LOW', value: 'PNG', displayValue: 'Low (Png image)', toolTip: 'Select for low bandwidth internet connections.'}
            ],
            VISIBILITY_ATTRIBUTES: {
                VISIBLE: 'visible',
                HIDDEN_ON_ENLARGEMENTS: 'hiddenonenlargements'
            },
            PRINT_PREVIEW_ACTIVE_WIDGETS: ['DisplayPreferences', 'DisplayMode', 'ExportSurveyData'],
            SURROUND_POLYGON_TYPE: 97
        };
    }
}
